import React, { useEffect, useRef, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import { BsArrowsFullscreen, BsFullscreenExit, BsVolumeMuteFill, BsVolumeUpFill } from "react-icons/bs";
import Leftsidebar from "../sidebar/leftsidebar";
import LeftResponsivesidebar from "../sidebar/left-responsive";
import Headmenu from "../sidebar/headmenu";
import viewCartIcon from "../../assets/images/icons/view-cart.svg";
import profileimg from "../../assets/profile.png";
import { addToCart, getCart } from "../../api/cartApi";
import useLiveViewer from "../../hook/useLiveViewer";
import {
  FeaturedBadge,
  formatPrice,
  LiveCommentsPanel,
  LiveProductCard,
  useLiveComments,
  useLiveProducts,
  useNow,
} from "./LiveShoppingParts";

const getUnavailableLabel = (product, now) => {
  if (product.endsAt && new Date(product.endsAt).getTime() <= now) return "Offer Ended";
  if (product.remainingQuantity === 0) return "Sold Out";
  return product.isAvailable ? null : "Unavailable";
};

const LiveShoppingView = () => {
  const { streamId } = useParams();
  const navigate = useNavigate();
  const [muted, setMuted] = useState(true);
  const [theater, setTheater] = useState(false);
  const [cartCount, setCartCount] = useState(0);
  const [addingId, setAddingId] = useState(null);
  const videoRef = useRef(null);
  const { stream, status, statusMessage, viewerCount, chatEnabled, socket } = useLiveViewer(streamId, "marketplace", videoRef);

  const now = useNow();
  const { comments, sendComment } = useLiveComments(stream && streamId, socket);
  const { products } = useLiveProducts(stream && streamId, socket);

  useEffect(() => {
    const loadCartCount = () =>
      getCart()
        .then((res) => setCartCount(Number(res?.result?.summary?.total_quantity) || 0))
        .catch(() => setCartCount(0));
    loadCartCount();
    window.addEventListener("cartUpdated", loadCartCount);
    return () => window.removeEventListener("cartUpdated", loadCartCount);
  }, []);

  useEffect(() => {
    if (!theater) return;
    const onKeyDown = (e) => e.key === "Escape" && setTheater(false);
    window.addEventListener("keydown", onKeyDown);
    return () => window.removeEventListener("keydown", onKeyDown);
  }, [theater]);

  const handleAddToCart = async (product, goToCart) => {
    setAddingId(product.liveStreamProductId);
    try {
      const data = await addToCart(Number(product.productId), 1, 0, product.liveStreamProductId);
      if (!data.success) throw new Error(data.message || "Unable to add to cart");
      if (goToCart) navigate("/cart");
      else toast.success("Added to cart");
    } catch (err) {
      toast.error(err.message);
    } finally {
      setAddingId(null);
    }
  };

  const renderBuyButton = (product, label, className) => {
    const unavailableLabel = getUnavailableLabel(product, now);
    return (
      <button
        onClick={() => handleAddToCart(product, label === "Buy Now")}
        disabled={!!unavailableLabel || addingId !== null}
        className={`flex items-center justify-center gap-1 rounded-full px-3 py-1 text-[11px] font-semibold uppercase text-white disabled:bg-gray-400 ${className}`}
      >
        <img src={viewCartIcon} alt="" className="h-3 w-3" />
        {unavailableLabel || label}
      </button>
    );
  };

  const viewCartButton = (
    <button
      onClick={() => navigate("/cart")}
      className="relative flex items-center gap-2 rounded-full bg-[#095228] px-4 py-2 text-sm text-white"
    >
      <img src={viewCartIcon} alt="" className="h-4 w-4" />
      View Cart
      {cartCount > 0 && (
        <span className="absolute -right-2 -top-2 rounded-full bg-[#F5D90A] px-1.5 text-[10px] font-bold text-black">
          {cartCount}
        </span>
      )}
    </button>
  );

  const sellerBadge = stream && (
    <div className="flex items-center gap-2 rounded-br-2xl bg-white/60 px-4 py-2 text-black backdrop-blur-sm">
      <img
        src={stream.user?.profile_picture_url || profileimg}
        alt=""
        className="h-9 w-9 rounded-full bg-white object-cover"
      />
      <div>
        <p className="text-sm font-semibold">
          {[stream.user?.first_name, stream.user?.last_name].filter(Boolean).join(" ")}
        </p>
        {stream.started_at && (
          <p className="text-[10px]">
            {new Date(stream.started_at).toLocaleString("en-US", { dateStyle: "medium", timeStyle: "short" })}
          </p>
        )}
      </div>
    </div>
  );


  return (
    <div className="dashboard w-full min-h-screen flex">
      <Leftsidebar />
      <main className="flex-1 min-w-0 p-4 md:p-6">
        <LeftResponsivesidebar />
        <Headmenu />

        <div className="mt-6 flex items-center justify-between">
          <h1 className="text-lg font-semibold">GoLive</h1>
          {viewCartButton}
        </div>

        <div className="mt-4 flex flex-col gap-4 lg:flex-row">
          <div className="min-w-0 flex-1 rounded-xl bg-[#EBF8ED]">
            <div
              className={
                theater
                  ? "fixed inset-0 z-50 bg-black text-white"
                  : "relative h-[260px] overflow-hidden rounded-xl bg-black text-white sm:h-[400px]"
              }
            >
              <video ref={videoRef} autoPlay playsInline muted={muted} className="h-full w-full object-cover" />

              {statusMessage && (
                <div className="absolute inset-0 flex items-center justify-center bg-black/70 px-6 text-center text-lg font-semibold">
                  {statusMessage}
                </div>
              )}

              <div className="absolute left-0 top-0">
                {sellerBadge}
                {theater && <p className="px-4 pt-3 text-sm font-semibold">{viewerCount} Viewers</p>}
              </div>

              {theater ? (
                <>
                  <div className="absolute right-4 top-4 flex items-center gap-3">
                    {viewCartButton}
                    {status === "live" && (
                      <span className="rounded-full bg-[#BE1F2E] px-5 py-1 text-sm font-semibold">LIVE</span>
                    )}
                    <button onClick={() => setTheater(false)} title="Exit full screen" className="rounded-full bg-black/50 p-2 text-white">
                      <BsFullscreenExit />
                    </button>
                  </div>

                  <LiveCommentsPanel
                    comments={comments}
                    chatEnabled={chatEnabled}
                    onSend={sendComment}
                    className="absolute bottom-4 right-4 h-[55%] w-[300px] max-w-[45%] rounded-xl bg-white/85 p-3 text-black"
                  />

                  <div className="absolute bottom-4 left-4 flex max-w-[calc(100%-340px)] gap-3 overflow-x-auto pt-2">
                    {products.map((product) => (
                      <div
                        key={product.liveStreamProductId}
                        className={`relative flex w-[280px] shrink-0 gap-3 rounded-xl bg-white/85 p-2 text-black ${
                          product.isFeatured ? "border-2 border-[#095228]" : ""
                        }`}
                      >
                        {product.isFeatured && <FeaturedBadge className="absolute -top-2 right-2" />}
                        {product.offerPercentage > 0 && (
                          <span className="absolute left-0 top-0 rounded-br-lg rounded-tl-xl bg-[#BE1F2E] px-2 text-[9px] font-semibold text-white">
                            {product.offerPercentage}% Offer
                          </span>
                        )}
                        <img
                          src={product.thumbnailUrl || "/image/default.png"}
                          alt=""
                          className="h-20 w-20 shrink-0 rounded-lg bg-[#ECEEF0] object-contain"
                        />
                        <div className="min-w-0 space-y-1">
                          <p className="line-clamp-2 text-[11px] font-bold uppercase">{product.name}</p>
                          <p className="text-sm font-bold text-[#095228]">{formatPrice(product.livePrice ?? product.discountedPrice)}</p>
                          {renderBuyButton(product, "Add to Cart", "bg-[#095228]")}
                        </div>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <>
                  <button
                    onClick={() => setTheater(true)}
                    title="Full screen"
                    className="absolute right-3 top-3 rounded-full bg-black/50 p-2 text-white"
                  >
                    <BsArrowsFullscreen />
                  </button>
                  <span className="absolute bottom-4 left-4 text-sm font-semibold">{viewerCount} Viewers</span>
                  {status === "live" && (
                    <span className="absolute bottom-4 right-4 rounded-full bg-[#BE1F2E] px-4 py-1 text-xs font-semibold">
                      LIVE
                    </span>
                  )}
                </>
              )}
            </div>

            <div className="flex items-center gap-3 p-3">
              <button
                onClick={() => setMuted((value) => !value)}
                title={muted ? "Unmute" : "Mute"}
                className="rounded-full bg-white p-3 text-gray-600"
              >
                {muted ? <BsVolumeMuteFill /> : <BsVolumeUpFill />}
              </button>
            </div>
          </div>

          <LiveCommentsPanel
            comments={comments}
            chatEnabled={chatEnabled}
            onSend={sendComment}
            className="h-[400px] w-full rounded-xl bg-[#F9FAFB] p-4 lg:h-[470px] lg:w-[300px]"
          />
        </div>

        {products.length > 0 && (
          <div className="mt-6 flex w-full gap-4 overflow-x-auto pb-2">
            {products.map((product) => (
              <LiveProductCard key={product.liveStreamProductId} product={product} now={now} className="w-[210px] shrink-0">
                {renderBuyButton(product, "Buy Now", "ml-auto bg-[#1F2937]")}
              </LiveProductCard>
            ))}
          </div>
        )}
      </main>
    </div>
  );
};

// Keyed by the live so opening another live starts with fresh state
const LiveShopping = () => {
  const { streamId } = useParams();
  return <LiveShoppingView key={streamId} />;
};

export default LiveShopping;
